import React, { useEffect, useRef, useState } from 'react';
import { useIntl } from 'react-intl';
import { useDispatch, useSelector } from 'react-redux';
import { localize, Logger, useDetectScroll } from '@saviynt/common';
import {
  Box,
  Button,
  ButtonIcon,
  FooterActionBar,
  Icon,
  InlineMessage,
  MobileDrawer,
  ModalIsland,
  Typography,
} from '@saviynt/design-system';
import cx from 'classnames';
import PropTypes from 'prop-types';

import useResponsive from '../../../../hooks/use-responsive';
import { setCertsDetailListLoading } from '../../../../redux/certdetails/action';
import {
  callReassignAPI,
  getPredefinedComments,
  setCertsListActionStatus,
} from '../../../../redux/certlist/action';
import { useInvertedLocalize } from '../../../../utils/localization';
import { getMessageKey } from '../../../../utils/utils';
import CertsModalCommentBox from '../../../CertsModalCommentBox';
import CertsUserListDropdown from '../../../CertsUserListDropdown';
import { isEmptyObject } from '../../../CertsUserListDropdown/utils';
import {
  ACT_REASSIGN,
  CERTS_LIST_PREDEFINED_COMMENTS_FOR_ACTION,
} from '../../constant';

import invertedMessages from './messages';

import './CertsListReassign.css';

function CertsListReassign({ isOpen, setIsOpen, certKey, tileData }) {
  const intl = useIntl();
  const dispatch = useDispatch();
  const [commentDescription, setCommentDescription] = useState('');
  const [predefComments, setPredefComments] = useState([]);
  const [selectedUser, setSelectedUser] = useState(null);
  const [isSelectUserCritical, setIsSelectUserCritical] = useState(false);
  const [showAlert, setShowAlert] = useState(false);

  const {
    core: { predefinedComments } = {},
    lineAction: { certsActionIsLoading, certsActionStatus } = {},
  } = useSelector((state) => state?.certifications?.list);

  const { messages } = useSelector((state) => state?.user);

  const closeButtonRef = useRef(null);
  const { isMobileXlargeWidth } = useResponsive();
  const { isScrolled } = useDetectScroll();

  const l = useInvertedLocalize(invertedMessages);

  const title =
    messages.CERTS_LIST_ACTION_REASSIGN_TITLE ||
    localize(intl, getMessageKey('CERTS_LIST_ACTION_REASSIGN_TITLE'));
  const subtitle =
    messages.CERTS_LIST_ACTION_REASSIGN_SUBTITLE ||
    localize(intl, getMessageKey('CERTS_LIST_ACTION_REASSIGN_SUBTITLE'));
  const headerIcon = () => (
    <Icon kind='Reassign' color='neutral-1000' size='large' />
  );

  const isPredefinedCommentsForCertKeyExists = (key) => {
    if (!predefinedComments) return false;

    const certKeyStr = key.toString();

    if (certKeyStr in predefinedComments) {
      const comments = predefinedComments[certKeyStr].CERTLISTREASSIGN;

      if (comments?.length) {
        setPredefComments(comments);

        return true;
      }
    }

    return false;
  };

  const { certifierUserKey = null, certificationConfig = null } =
    tileData || {};
  const isCommentRequired = certificationConfig?.reassignComments;

  useEffect(() => {
    if (isOpen && !isPredefinedCommentsForCertKeyExists(certKey)) {
      dispatch(
        getPredefinedComments(
          CERTS_LIST_PREDEFINED_COMMENTS_FOR_ACTION.CERTS_LIST_REASSIGN,
          certKey
        )
      );
    }
  }, [isOpen]);

  useEffect(() => {
    isPredefinedCommentsForCertKeyExists(certKey);
  }, [predefinedComments]);

  const handleClose = () => {
    setIsOpen(null);
    setIsSelectUserCritical(false);
    setSelectedUser(null);
    setShowAlert(false);
    dispatch(setCertsDetailListLoading(false));

    if (certsActionStatus?.status === 'FAILED') {
      dispatch(setCertsListActionStatus(null));
    }
  };

  const isSubmitButtonEnable = () => {
    const isReassingUserSelected = selectedUser !== null;

    if (isCommentRequired) {
      const isCommentLengthGreaterThan3 =
        commentDescription?.replace(/[<>]/g, '')?.trim()?.length >= 3;
      const hasAngleBrackets = /<|>/.test(commentDescription);

      return (
        isCommentLengthGreaterThan3 &&
        !hasAngleBrackets &&
        isReassingUserSelected
      );
    }

    return isReassingUserSelected;
  };

  const handleSumbit = async () => {
    const noCommentProvided =
      isCommentRequired && commentDescription.length === 0;
    const isUserSelected = !selectedUser?.id;

    if (noCommentProvided || !isSubmitButtonEnable()) setShowAlert(true);
    if (isUserSelected) setIsSelectUserCritical(true);

    if (noCommentProvided || isUserSelected || !isSubmitButtonEnable()) return;

    const payload = {
      certkey: certKey,
      assignee: selectedUser?.id,
    };

    if (commentDescription) {
      payload.comments = commentDescription;
    }

    try {
      const result = await dispatch(callReassignAPI(payload));

      if (result.success) {
        handleClose();
      } else {
        Logger.error('Reassign API call failed');
      }
    } catch (error) {
      Logger.error('Reassign API call error:', error);
    }
  };

  const getReassignSelectCertifier = () => (
    <Box className='CertsListReassign-selectCertifier'>
      <div className='CertsListReassign-selectCertifier-title'>
        <Typography
          kind='h3'
          desktop='h3-d'
          mobile='h3-d'
          className='CertsListReassign-selectCertifier-asterisk'>
          *
        </Typography>
        <Typography
          kind='h4'
          mobile='h4-d'
          className='CertsReassignModal-container-certifier'>
          {messages.REASSIGN_SELECT_CERTIFIER ||
            localize(intl, getMessageKey('REASSIGN_SELECT_CERTIFIER'))}
        </Typography>
      </div>

      <CertsUserListDropdown
        className='CertsListReassignModal-dropdown'
        defaultReassignee=''
        defaultAssigneName=''
        onSelect={(user) => {
          if (user && !isEmptyObject(user)) {
            setSelectedUser(user);
          } else {
            setSelectedUser(null);
          }

          setIsSelectUserCritical(false);
        }}
        selectedUser={selectedUser}
        certKey={certKey}
        showDefaultCertifier
        isCritical={isSelectUserCritical}
        placeholder={
          messages.REASSSIGN_PLACEHOLDER_LABEL ||
          localize(intl, getMessageKey('REASSSIGN_PLACEHOLDER_LABEL'))
        }
        certifierUserKey={certifierUserKey}
      />
    </Box>
  );

  const isAPIfailed =
    certsActionStatus?.status === 'FAILED' &&
    certsActionStatus?.action === ACT_REASSIGN;

  const APIFailedMessage =
    messages.CERTS_LIST_ACTION_API_FAILURE ||
    localize(intl, getMessageKey('CERTS_LIST_ACTION_API_FAILURE'));

  const commentBoxWrapperClasses = cx(
    'CertsListReassignCommentBox',
    !isCommentRequired && 'CertsListReassignCommentBox--noComments'
  );

  const getReassignContentBody = () => (
    <>
      <InlineMessage
        kind='background'
        text={APIFailedMessage}
        colorTheme='critical'
        size='medium'
        isVisible={isAPIfailed}
        leftIcon={<Icon kind='AlertCriticalFilled' />}
        className='CertsListActionAPIFailure'
      />
      {getReassignSelectCertifier()}
      {isCommentRequired && (
        <Box className={commentBoxWrapperClasses}>
          <CertsModalCommentBox
            defaultComments={predefComments}
            onChange={setCommentDescription}
            showAlert={showAlert}
            isRequired={isCommentRequired}
            allowEditPredefinedComments={
              predefinedComments[certKey.toString()]
                ?.allowEditPredefinedComments
            }
          />
        </Box>
      )}
    </>
  );

  const getReassignFooter = () => (
    <FooterActionBar
      className='CertsListReassign-footer'
      size='large'
      isScrolled={isScrolled}>
      <div className='CertsListReassign-actionButtons'>
        <Button
          type='button'
          kind='ghost'
          size='medium'
          isDisabled={false}
          onClick={handleClose}>
          {messages.CERTS_CANCEL ||
            localize(intl, getMessageKey('CERTS_CANCEL'))}
        </Button>
        <Button
          className='CertsListReassign-submitButton'
          type='button'
          kind={isSubmitButtonEnable() ? 'filled' : 'outlined'}
          size='medium'
          onClick={handleSumbit}
          isLoading={certsActionIsLoading}
          loaderText={
            messages.CERTS_LOADING ||
            localize(intl, getMessageKey('CERTS_LOADING'))
          }>
          {messages.CERTS_SUBMIT ||
            localize(intl, getMessageKey('CERTS_SUBMIT'))}
        </Button>
      </div>
    </FooterActionBar>
  );

  if (isMobileXlargeWidth) {
    const getHeaderContentMobile = () => (
      <>
        <div className='CertsListDiscontinue-mobileHeader'>
          {headerIcon()}
          <div className=''>
            <Typography kind='h1' desktop='h1-d' mobile='h1-d'>
              {title}
            </Typography>
            <Typography kind='body3' desktop='body3-d' mobile='body3-d'>
              {subtitle}
            </Typography>
          </div>
        </div>

        <ButtonIcon
          ref={closeButtonRef}
          size='medium'
          kind='ghost'
          className='Example-MobileDrawer-closeButton'
          ariaLabel={l('Close')}
          icon={<Icon kind='Close' />}
          onClick={handleClose}
        />
      </>
    );

    return (
      <MobileDrawer
        isOpen={isOpen}
        setIsOpen={setIsOpen}
        isCloseButtonEnabled
        content={
          <Box className='CertsListReassign-mobileBody'>
            {getReassignContentBody()}
          </Box>
        }
        headerContent={getHeaderContentMobile()}
        footerActionBar={getReassignFooter()}
        className='CertsListReassign-mobileDrawer'
        classNames={{
          contentClassName: 'CertsListReassign-mobileDrawer--content',
          headerClassName: 'CertsListReassign-mobileDrawer--header',
        }}
      />
    );
  }

  return (
    <ModalIsland
      isOpen={isOpen}
      onClose={handleClose}
      headerIcon={headerIcon()}
      title={title}
      subtitle={subtitle}
      contentBody={getReassignContentBody()}
      FooterActionBarComp={getReassignFooter()}
      className='CertsListReassign'
      isOverflowVisible={!isCommentRequired}
    />
  );
}

CertsListReassign.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  setIsOpen: PropTypes.func.isRequired,
  certKey: PropTypes.number.isRequired,
  tileData: PropTypes.shape({}),
};

CertsListReassign.defaultProps = {
  tileData: null,
};

export default CertsListReassign;
